/**
 * API client for the Node/Express reset backend.
 *
 * Base URL is env-driven (VITE_API_BASE_URL), inlined by Vite at build time.
 * Behind nginx the SPA and API share the secret domain, so the default is `/api`.
 *
 * Endpoints (see DESIGN.md backend_contract):
 *   GET  {base}/validate-token?token=<rawhex>   -> always HTTP 200, enumeration-neutral
 *   POST {base}/submit-new-password { token, password }
 */

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/+$/, '')

/** A raw reset token is exactly 64 lowercase hex chars (SHA-256 of 32 CSPRNG bytes on the wire). */
export const TOKEN_PATTERN = /^[0-9a-f]{64}$/

export function isWellFormedToken(token: string | null | undefined): token is string {
  return typeof token === 'string' && TOKEN_PATTERN.test(token)
}

export type ValidateReason = 'invalid' | 'expired' | 'used'

export interface ValidateResult {
  valid: boolean
  /** Present only when valid === false. */
  reason?: ValidateReason
}

/**
 * GET /validate-token
 *
 * The backend always answers HTTP 200 with { valid } to avoid enumeration.
 * A malformed token is rejected client-side without a network call (mirrors
 * the backend's own short-circuit) and reported as reason "invalid".
 * Any transport/HTTP failure is surfaced by throwing (caller shows a network error).
 */
export async function validateToken(token: string): Promise<ValidateResult> {
  if (!isWellFormedToken(token)) {
    return { valid: false, reason: 'invalid' }
  }

  const res = await fetch(
    `${API_BASE_URL}/validate-token?token=${encodeURIComponent(token)}`,
    {
      method: 'GET',
      headers: { Accept: 'application/json' },
    },
  )

  if (res.status === 429) {
    throw new RateLimitError()
  }
  if (!res.ok) {
    throw new Error(`Unexpected status ${res.status}`)
  }

  const data = (await res.json()) as ValidateResult
  return data
}

export type SubmitOutcome =
  | { status: 'success' }
  | { status: 'weak_password'; message?: string }
  | { status: 'invalid_token' }
  | { status: 'rate_limited' }
  | { status: 'server_error' }
  | { status: 'network_error' }

/** Thrown internally for 429 so callers can map it uniformly. */
export class RateLimitError extends Error {
  constructor() {
    super('rate_limited')
    this.name = 'RateLimitError'
  }
}

/**
 * POST /submit-new-password
 *
 * Maps the backend contract to a discriminated union the UI can switch on:
 *   200                       -> success
 *   400 error=weak_password   -> weak_password (keep form, inline error)
 *   400 error=invalid_token   -> invalid_token (switch to invalid/expired screen)
 *   429 error=rate_limited    -> rate_limited
 *   500 error=server_error    -> server_error (generic + retry)
 *   transport failure         -> network_error
 */
export async function submitNewPassword(
  token: string,
  password: string,
): Promise<SubmitOutcome> {
  let res: Response
  try {
    res = await fetch(`${API_BASE_URL}/submit-new-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ token, password }),
    })
  } catch {
    return { status: 'network_error' }
  }

  if (res.status === 200) {
    return { status: 'success' }
  }
  if (res.status === 429) {
    return { status: 'rate_limited' }
  }

  // 400 / 500 (and any other non-2xx): read the error discriminator if present.
  let body: { success?: boolean; error?: string; message?: string } = {}
  try {
    body = await res.json()
  } catch {
    // Non-JSON body; fall through to status-based mapping below.
  }

  if (res.status === 400) {
    if (body.error === 'weak_password') {
      return { status: 'weak_password', message: body.message }
    }
    // invalid_token (or any other 400) -> treat as an invalid/expired link.
    return { status: 'invalid_token' }
  }

  if (body.error === 'rate_limited') {
    return { status: 'rate_limited' }
  }

  // 500 and anything else unexpected.
  return { status: 'server_error' }
}
